import { get, post, type ReqConfig } from '../request';

/** POST /broadcasts {content}：公告广播给全部在线坐席 */
export function broadcast(content: string) {
  return post('/broadcasts', { content });
}

/** GET /announcements/recent?window_min=10：拉最近公告（坐席连接/重连后补拉，auth 全角色） */
export function recentAnnouncements(windowMin = 10) {
  return get(`/announcements/recent?window_min=${windowMin}`);
}

/**
 * POST /monitor/calls/:uuid/action {mode}：呼叫监听开关。
 * listen 为幂等续期——后端订阅带 TTL，弹窗开着就要周期性重发（见 MonitorModal 的续期定时器）；
 * stop 结束监听（同时停音频 fork）。
 * 为什么必须显式开关：通话内容（实时转写/情绪/质检等读端点）只对「本人承办 / admin / 正在监听该路」放行，
 * 未监听时读内容端点一律 404（见本文件各 get* 的「归属校验」说明）。
 */
export function monitorCallAction(uuid: string, mode: 'listen' | 'stop', cfg?: ReqConfig) {
  return post(`/monitor/calls/${uuid}/action`, { mode }, cfg);
}

/** POST /monitor/chats/:session_id/action {mode}：在线会话监听开关（无音频，仅订阅内容事件；语义同 monitorCallAction） */
export function monitorChatAction(sessionId: number, mode: 'listen' | 'stop', cfg?: ReqConfig) {
  return post(`/monitor/chats/${sessionId}/action`, { mode }, cfg);
}

/** POST /calls/queue-pull {queue, uuid, agent_id}：手动拉取排队客户强插 */
export function queuePull(p: { queue: string; uuid: string; agent_id: number }) {
  return post('/calls/queue-pull', p);
}

/** 坐席实时状态行（GET /monitor/agents） */
export interface MonitorAgent {
  agent_id: number;
  name?: string;
  extension?: string;
  skill_group?: string;
  /** idle|busy|talking|ringing|break|after_work|offline（兼容 acw 别名） */
  state?: string;
  /** 当前状态已持续秒数（页面按拉取时刻本地递增；离线不再归零） */
  state_duration_sec?: number;
  today_calls?: number;
  today_chats?: number;
  /** 当前通话/振铃主叫号码 */
  caller?: string;
  /** 当前通话已接通时长（秒；振铃/未应答为 0） */
  call_duration_sec?: number;
  /** 空闲时长（idle 状态持续） */
  idle_duration_sec?: number;
  /** 今日通话总时长（秒） */
  today_talk_sec?: number;
  /** 本次上线时间（Unix 秒，0=未在线） */
  online_at?: number;
  /** 生效接线模式（mixed/voice_only/chat_only）：chat_only 示闲不代表可接电话 */
  channel_mode?: string;
  [k: string]: any;
}

/** 队列实况行（GET /monitor/queues） */
export interface MonitorQueue {
  /** 技能组显示名（界面展示） */
  name: string;
  /** 队列键（skill_group.code）：暂停/恢复与排队明细都按它调后端 */
  code?: string;
  /** 语音排队数（兼容旧字段） */
  waiting?: number;
  /** 在线排队会话数（v1.6 统一路由） */
  waiting_chat?: number;
  longest_wait_sec?: number;
  agents_total?: number;
  agents_idle?: number;
  /** 队列暂停 */
  paused?: boolean;
  /** 队列状态：normal 正常 / overflow 溢出 / full 已满 / paused 暂停 */
  status?: string;
  /** 今日进线总量 */
  today_inbound?: number;
  queue_timeout_sec?: number;
  [k: string]: any;
}

/** 在途呼叫行（GET /monitor/calls） */
export interface MonitorCall {
  uuid: string;
  caller?: string;
  callee?: string;
  direction?: string; // inbound|outbound
  queue?: string;
  agent_id?: number;
  agent_name?: string;
  start_time?: string;
  answered?: boolean;
  duration_so_far_sec?: number;
  /** 通话流拓扑（1.2）：AI 会话标识（true=AI 机器人接待中/已转人工的 AI 通话） */
  ai_session?: boolean;
  /**
   * 通话阶段（注册表 `stage` 字段）：空=正常在途（排队/振铃/坐席通话中）；
   * `survey`=坐席或 AI 已离场、客户停在满意度问询——通道仍活着（等着按键，最长数分钟），
   * 但已不是「通话中」：不能计入「N 通在途」，状态列要单独标注。
   */
  stage?: string;
  [k: string]: any;
}

/** 大屏最近呼叫行（screen/overview.recent_calls） */
export interface ScreenRecentCall {
  caller?: string;
  callee?: string;
  direction?: string;
  start_time?: string;
  billsec?: number;
  hangup_cause?: string;
  agent_name?: string;
  [k: string]: any;
}

/** 大屏聚合（GET /monitor/screen/overview） */
export interface ScreenOverview {
  agents?: MonitorAgent[];
  queues?: MonitorQueue[];
  today?: {
    calls?: number;
    ai_sessions?: number;
    connected_rate?: number;
    satisfaction_avg?: number;
  };
  recent_calls?: ScreenRecentCall[];
  [k: string]: any;
}

function asArray<T>(data: any): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray(data.list)) return data.list as T[];
  return [];
}

/** GET /monitor/agents；轮询场景传 silent=true 避免错误刷屏 */
export async function listMonitorAgents(silent = false): Promise<MonitorAgent[]> {
  return asArray<MonitorAgent>(await get('/monitor/agents', undefined, { silent }));
}

/** GET /monitor/queues */
export async function listMonitorQueues(silent = false): Promise<MonitorQueue[]> {
  return asArray<MonitorQueue>(await get('/monitor/queues', undefined, { silent }));
}

/** GET /monitor/calls */
export async function listMonitorCalls(silent = false): Promise<MonitorCall[]> {
  return asArray<MonitorCall>(await get('/monitor/calls', undefined, { silent }));
}

/** 在途 AI 会话实时摘要（GET /monitor/ai-stream，「AI 动态流」，admin+supervisor） */
export interface AIStreamItem {
  uuid: string;
  caller?: string;
  direction?: string;
  bot_name?: string;
  intent?: string;
  /** 受控意图摘要（列表正文展示这一项；原文不下发，故不会为空到无话可说） */
  intent_note?: string;
  /** ASR 原文：后端已不下发（保留字段兼容旧响应）；通话原文只在显式监听该路后经监听通道可见 */
  text?: string;
  started_at?: number;
  duration_sec?: number;
}

export async function listAIStream(silent = false): Promise<AIStreamItem[]> {
  return asArray<AIStreamItem>(await get('/monitor/ai-stream', undefined, { silent }));
}

/**
 * GET /monitor/screen/overview
 * 大屏免登模式：token 存在时随 query 传递（只读 token），无 token 走 JWT 拦截器。
 */
export function getScreenOverview(token?: string, silent = false): Promise<ScreenOverview> {
  return get('/monitor/screen/overview', token ? { token } : undefined, { silent });
}

// ---------- 转人工结构化上下文 ----------

/** 转人工上下文已完成步骤（AI 阶段动作账本） */
export interface TransferStep {
  code: string;
  desc: string;
}

/** 转人工上下文外部数据源查询结果 */
export interface TransferQueryResult {
  query: string;
  status: string;
  result: string;
  /** 动作中文短名（后端按数据源配置生成；旧快照缺省，展示回退 query code） */
  name?: string;
  /** 坐席可读结果文本（播报模板自然语言，无模板则「字段：值」逐行；旧快照缺省，展示回退 result） */
  text?: string;
}

/** 转人工上下文已采集建单字段 */
export interface TransferCollected {
  key: string;
  label: string;
  value: string;
  status: string;
}

/** 转人工结构化上下文（AI 阶段 意图/诉求/查询结果/步骤/采集字段，坐席弹屏直达） */
export interface TransferContext {
  intent?: string;
  problem?: string;
  transfer_reason?: string;
  reason_detail?: string;
  emotion?: string;
  risk_level?: string;
  query_results?: TransferQueryResult[];
  completed_steps?: TransferStep[];
  collected_fields?: TransferCollected[];
  duration_sec?: number;
  turn_count?: number;
  ts?: number;
  [k: string]: any;
}

/** transfer_reason 中文映射 */
export const TRANSFER_REASON_LABELS: Record<string, string> = {
  customer_request: '客户主动要求',
  intent_transfer: '意图转人工',
  emotion: '情绪熔断',
  risk: '风险熔断',
  fault: 'AI 故障兜底',
  interface_error: '接口异常兜底',
  scope_exceeded: '超出自助范围',
};

/**
 * GET /monitor/calls/:uuid/transfer-context（auth 全角色 + 归属校验）
 * 工作台坐席转人工后定向拉取结构化上下文；无上下文或非本人归属返回 null。
 */
export async function getTransferContext(uuid: string): Promise<TransferContext | null> {
  try {
    const r = await get<{ context: TransferContext | null }>(`/monitor/calls/${uuid}/transfer-context`, undefined, {
      silent: true,
    });
    return r?.context || null;
  } catch {
    return null;
  }
}

/**
 * GET /monitor/calls/:uuid/live-segments（auth 全角色 + 归属校验）
 * 在途通话 KeyCallSegments 快照（含 seq），供坐席端 seq 对账回填；失败/无权返回 []。
 */
export async function getLiveSegments(uuid: string): Promise<unknown[]> {
  try {
    const r = await get<{ segments: unknown[] }>(`/monitor/calls/${uuid}/live-segments`, undefined, {
      silent: true,
    });
    return r?.segments || [];
  } catch {
    return [];
  }
}

/** 通话情绪序列行（GET /monitor/calls/:uuid/emotion-series；字段与质检域同源） */
export interface CallEmotionSeriesRow {
  id: number;
  emotion?: string;
  level?: string;
  complaint_type?: string;
  escalation_prob?: number;
  utterance?: string;
  created_at?: string;
}

/**
 * GET /monitor/calls/:uuid/emotion-series（auth 全角色 + 归属校验）
 * 坐席读本通情绪采样：工作台在通话开始/重连后回填情绪趋势（此前仅 quality.manage 可读，坐席 403）。
 * 失败/无权返回 []。
 */
export async function getCallEmotionSeries(uuid: string): Promise<CallEmotionSeriesRow[]> {
  try {
    const r = await get<CallEmotionSeriesRow[] | { list?: CallEmotionSeriesRow[] }>(
      `/monitor/calls/${uuid}/emotion-series`,
      undefined,
      { silent: true }
    );
    if (Array.isArray(r)) return r;
    return r?.list || [];
  } catch {
    return [];
  }
}

/** 本通最新实时识别快照（GET /monitor/calls/:uuid/assist-analysis；assist_analysis 每通一条） */
export interface CallAssistAnalysisRow {
  intent?: string;
  intent_code?: string;
  intent_conf?: number;
  emotion?: string;
  emotion_level?: string;
  risk_level?: string;
  risk_reason?: string;
  updated_at?: string;
}

/**
 * GET /monitor/calls/:uuid/assist-analysis（auth 全角色 + 归属校验）
 * 坐席刷新/断线重连后回填意图/情绪/风险（左栏「意图」卡 + 工具条情绪胶囊与风险按钮）；无快照返回 null。
 */
export async function getCallAssistAnalysis(uuid: string): Promise<CallAssistAnalysisRow | null> {
  try {
    const r = await get<CallAssistAnalysisRow | null>(`/monitor/calls/${uuid}/assist-analysis`, undefined, {
      silent: true,
    });
    return r || null;
  } catch {
    return null;
  }
}

/** 在线会话最近一次实时识别快照（GET /monitor/chats/:id/assist-snapshot） */
export interface ChatAssistSnapshotRow {
  intent?: string;
  intent_code?: string;
  intent_conf?: number;
  emotion?: string;
  emotion_level?: string;
  risk_level?: string;
  risk_reason?: string;
  evidence?: string;
  ts?: number;
}

/**
 * GET /monitor/chats/:session_id/assist-snapshot（auth 全角色 + 归属校验）
 * 切会话/刷新后回填在线通道的意图/情绪/风险（左栏「意图」卡 + 工具条情绪胶囊与风险按钮）；无快照返回 null。
 */
export async function getChatAssistSnapshot(sessionId: number): Promise<ChatAssistSnapshotRow | null> {
  try {
    const r = await get<ChatAssistSnapshotRow | null>(`/monitor/chats/${sessionId}/assist-snapshot`, undefined, {
      silent: true,
    });
    return r || null;
  } catch {
    return null;
  }
}

/** 本通实时质检告警行（GET /monitor/calls/:uuid/quality-alerts；字段与质检域同源） */
export interface CallQualityAlertRow {
  id: number;
  rule_id?: number;
  name?: string;
  type?: string;
  dimension?: string;
  level?: string;
  deduct?: number;
  emotion?: string;
  complaint_type?: string;
  escalation_prob?: number;
  alert_type?: string; // rule|emotion|trend|rule_low_score
  /** 低分告警（rule_low_score）触发规则的得分（0–5）；其余告警为空 */
  awarded?: number | null;
  status?: string;
  /** 坐席打标（L1）：none|tp|fp|handled；质检员裁定后锁定 */
  feedback_verdict?: string;
  /** 质检员裁定（L2）：none|tp|fp */
  review_verdict?: string;
  /** 命中说明（服务端由证据反推，与实时载荷同义）：回填补拉时卡片仍能显示「为什么命中」 */
  reason?: string;
  created_at?: string;
}

/**
 * GET /monitor/calls/:uuid/quality-alerts（auth 全角色 + 归属校验）
 * 坐席读本通质检告警（含打标/裁定状态）：断线或刷新后补拉，逐条确认/误报。失败/无权返回 []。
 */
export async function getCallQualityAlerts(uuid: string): Promise<CallQualityAlertRow[]> {
  try {
    const r = await get<CallQualityAlertRow[] | { list?: CallQualityAlertRow[] }>(
      `/monitor/calls/${uuid}/quality-alerts`,
      undefined,
      { silent: true }
    );
    if (Array.isArray(r)) return r;
    return r?.list || [];
  } catch {
    return [];
  }
}

/**
 * GET /monitor/chats/:session_id/quality-alerts（auth 全角色 + 归属校验）
 * 坐席读当前在线会话的实时质检告警（与语音通道同形状）：切会话/刷新/断线后补拉。
 * 失败/无权返回 []。
 */
export async function getChatQualityAlerts(sessionId: number): Promise<CallQualityAlertRow[]> {
  try {
    const r = await get<CallQualityAlertRow[] | { list?: CallQualityAlertRow[] }>(
      `/monitor/chats/${sessionId}/quality-alerts`,
      undefined,
      { silent: true }
    );
    if (Array.isArray(r)) return r;
    return r?.list || [];
  } catch {
    return [];
  }
}

/**
 * POST /monitor/calls/:uuid/quality-alerts/:id/feedback —— 坐席对单条告警打标（L1，可改）：
 * tp=准确 / fp=误报 / handled=已处理。质检员裁定后不可改（后端 400）。
 */
export async function feedbackQualityAlert(
  uuid: string,
  id: number,
  verdict: 'tp' | 'fp' | 'handled',
  note?: string
): Promise<CallQualityAlertRow> {
  const data: any = await post(`/monitor/calls/${uuid}/quality-alerts/${id}/feedback`, { verdict, note });
  return (data || {}) as CallQualityAlertRow;
}


// ---------- 组长现场动作（强拆 / 转接他人通话 / 强插三方 / 耳语辅导） ----------

/** 现场动作类型：kick=强拆 / transfer=转接 / three_way=强插三方 / whisper=耳语辅导 */
export type SuperviseMode = 'kick' | 'transfer' | 'three_way' | 'whisper';

export interface SuperviseInput {
  /** 转接目标分机（与 queue 二选一，分机优先） */
  target_extension?: string;
  /** 转接目标技能组队列键 */
  queue?: string;
}

export interface SuperviseResult {
  mode: SuperviseMode;
  uuid: string;
  queue?: string;
  extension?: string;
  detail?: string;
}

/**
 * POST /monitor/calls/:uuid/supervise
 * 组长对他人在途通话的现场干预。强插/耳语用登录人自己绑定分机（后端解析，前端不能指定别人）；
 * 未绑定分机时后端返回明确文案，不要吞掉——那是现场唯一的排障线索。
 */
export function superviseCall(uuid: string, mode: SuperviseMode, input?: SuperviseInput): Promise<SuperviseResult> {
  return post(`/monitor/calls/${uuid}/supervise`, { mode, ...(input || {}) });
}

// ---------- 坐席一键求助（举手） ----------

export interface SupportRequest {
  agent_id: number;
  agent_name: string;
  extension: string;
  skill_group: string;
  user_id: number;
  call_uuid?: string;
  chat_session_id?: number;
  note?: string;
  created_at: number;
}

/** POST /monitor/support-requests：坐席举手（带当前通话/会话，组长点进来就能直接介入） */
export function requestSupport(p: { call_uuid?: string; chat_session_id?: number; note?: string }) {
  return post('/monitor/support-requests', p);
}

/** GET /monitor/support-requests：组长拉当前在内的求助（15 分钟过期） */
export async function listSupportRequests(silent = false): Promise<SupportRequest[]> {
  return asArray<SupportRequest>(await get('/monitor/support-requests', undefined, { silent }));
}

/** POST /monitor/support-requests/:agent_id/resolve：组长处理完毕 */
export function resolveSupportRequest(agentId: number) {
  return post(`/monitor/support-requests/${agentId}/resolve`, {});
}